import type { CSSProperties } from "react";

/** The part of the visual viewport a dialog needs (`window.visualViewport`). */
export type VisualViewportBox = { height: number; offsetTop: number; scale: number };

/** Shrinks up to this much are browser chrome (the URL bar), not a keyboard. */
const KEYBOARD_THRESHOLD = 100;

/**
 * Whether a dialog's merged classes keep the default centring the keyboard
 * style moves. A dialog that positions itself, like the review photo viewer
 * (`top-0 translate-y-0`, which tailwind-merge keeps over the defaults), is
 * left alone.
 */
export function centresVertically(className: string | undefined): boolean {
  const classes = className?.split(/\s+/) ?? [];
  return classes.includes("top-[50%]") && classes.includes("translate-y-[-50%]");
}

/**
 * Where a dialog goes while an on-screen keyboard is open. Android Chrome
 * shrinks only the visual viewport for the keyboard, so a dialog centred in the
 * layout viewport (`fixed top-1/2 -translate-y-1/2`) stays put and its lower half
 * sits under the keyboard. This centres it in the visible area and caps its
 * height to fit, so a full-height dialog ends at the keyboard.
 *
 * With no keyboard (the visual viewport within 100px of the layout one) it
 * returns nothing, and the dialog's classes apply as before. Pinch zoom also
 * shrinks the visual viewport, but the user is panning around the page then, so
 * a zoomed view is left alone too.
 */
export function keyboardSafeStyle(
  visual: VisualViewportBox | null | undefined,
  layoutHeight: number,
): CSSProperties | undefined {
  if (!visual || visual.scale > 1.01) return undefined;
  if (layoutHeight - visual.height <= KEYBOARD_THRESHOLD) return undefined;
  return {
    top: visual.offsetTop + visual.height / 2,
    maxHeight: visual.height,
    overflowY: "auto",
  };
}
